import React, { useState, useRef, useEffect } from 'react';
import { Dropdown, Button } from 'react-bootstrap';
import { ThreeDotsVertical } from 'react-bootstrap-icons';
import { createPortal } from 'react-dom';

export interface ActionItem {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
    disabled?: boolean;
}

export interface ActionDropdownProps {
    actions: ActionItem[];
    size?: 'sm' | 'lg';
    className?: string;
    disabled?: boolean;
}

export function ActionDropdown({ actions, size = 'sm', className = '', disabled = false }: ActionDropdownProps) {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Calculate position when dropdown opens
    useEffect(() => {
        if (show && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const menuWidth = 150; // minWidth from the menu style
            const viewportWidth = window.innerWidth;
            
            // Calculate left position, ensuring it doesn't go off screen
            let leftPosition = rect.left + window.scrollX;
            if (leftPosition + menuWidth > viewportWidth) {
                leftPosition = rect.right + window.scrollX - menuWidth;
            }
            
            setPosition({
                top: rect.bottom + window.scrollY,
                left: leftPosition
            });
        }
    }, [show]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonRef.current && 
                !buttonRef.current.contains(event.target as Node) &&
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setShow(false);
            }
        };

        if (show) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [show]);

    // Handle escape key to close dropdown
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShow(false);
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscape);
            return () => {
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [show]);

    const handleToggle = () => {
        setShow(!show);
    };

    const handleActionClick = (action: ActionItem) => {
        action.onClick();
        setShow(false);
    };

    const menuContent = show ? (
        <div
            ref={menuRef}
            className="dropdown-menu show"
            style={{
                position: 'absolute',
                top: position.top,
                left: position.left,
                zIndex: 9999,
                minWidth: '150px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: 'white'
            }}
        >
            {actions.map((action, index) => (
                <div
                    key={index}
                    onClick={() => { if (!action.disabled) handleActionClick(action); }}
                    className={`d-flex align-items-center gap-2 px-3 py-2 ${
                        action.variant === 'danger' ? 'text-danger' : 
                        action.variant === 'warning' ? 'text-warning' :
                        action.variant === 'info' ? 'text-info' : ''
                    }`}
                    style={{
                        fontSize: '14px',
                        cursor: action.disabled ? 'not-allowed' : 'pointer',
                        borderBottom: index < actions.length - 1 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background-color 0.15s ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                        if (!action.disabled) e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    {action.icon && <span className="me-2" style={{ opacity: action.disabled ? 0.5 : 1 }}>{action.icon}</span>}
                    <span style={{ opacity: action.disabled ? 0.5 : 1 }}>{action.label}</span>
                </div>
            ))}
        </div>
    ) : null;

    return (
        <>
            <Button
                ref={buttonRef}
                variant="outline-primary"
                size={size}
                className={`border-primary border-1 status-btn text-black d-flex align-items-center px-2 py-1 ${className}`}
                disabled={disabled}
                onClick={handleToggle}
                style={{ 
                    minWidth: '32px',
                    height: '28px',
                    padding: '2px 6px'
                }}
            >
                <ThreeDotsVertical size={14} />
            </Button>
            {menuContent && createPortal(menuContent, document.body)}
        </>
    );
}